/**
 * The scan bar's focus and feedback machine.
 *
 * A wireless HID scanner is a keyboard: it types wherever focus happens to be.
 * So focus retention is not polish, it is correctness - a bar that quietly loses
 * focus sends a barcode into whatever else is on screen. Everything that can take
 * focus away (a tap on the background, a submit, a bottom sheet, a screen change)
 * is named here rather than left to the browser's defaults.
 *
 * The controller talks to a FocusTarget port, not an HTMLInputElement, so the
 * same rules drive the text field, the camera decode stream and the prototype's
 * simulate-scan chips without three divergent copies.
 */

export interface FocusTarget {
  focus(): void
  blur(): void
  read(): string
  clear(): void
}

export type ScanResult = 'accept' | 'reject'

export interface ScanContext {
  /** Where am I - the context chip in the bar. */
  context: string
  /** What the next scan does, in words. The halo says where, this says what. */
  nextAction: string
}

export interface LastScan {
  code: string
  result: ScanResult
  message: string
}

export interface ScanBarState extends ScanContext {
  mounted: boolean
  /** Set while a sheet has deliberately claimed focus; null when the bar owns it. */
  suspendedBy: string | null
  lastScan: LastScan | null
  /** Which tone to play, per the triple-redundant feedback rule. */
  sound: ScanResult | null
  /** True while a scan is in flight - the bar shows a hairline progress line, not a spinner. */
  inFlight: boolean
}

export interface Settlement {
  result: ScanResult
  message: string
  /** Where the halo goes next. On a reject the recovery action wins, per attention rule 3. */
  nextAction?: string
}

export interface ScanFocusOptions {
  onScan?: (code: string) => void
  onChange?: (state: ScanBarState) => void
  /** What the bar asks for after a rejected scan. */
  recoveryAction?: string
}

/** Codes are upper-cased and trimmed once, here, so no screen has to remember to. */
export function normaliseCode(raw: string): string {
  return raw.trim().toUpperCase()
}

export class ScanFocusController {
  private mounted = false
  private suspendedBy: string | null = null
  private context = ''
  private nextAction = ''
  private lastScan: LastScan | null = null
  private sound: ScanResult | null = null
  private inFlight = false
  /** The code currently awaiting a verdict, so a camera decoding 30fps fires once. */
  private pendingCode: string | null = null

  constructor(
    private readonly target: FocusTarget,
    private readonly options: ScanFocusOptions = {},
  ) {}

  state(): ScanBarState {
    return {
      mounted: this.mounted,
      suspendedBy: this.suspendedBy,
      context: this.context,
      nextAction: this.nextAction,
      lastScan: this.lastScan,
      sound: this.sound,
      inFlight: this.inFlight,
    }
  }

  mount(ctx: ScanContext): void {
    this.mounted = true
    this.context = ctx.context
    this.nextAction = ctx.nextAction
    this.claim()
  }

  /** A screen change keeps the field hot and only rewrites the words. */
  enterScreen(ctx: ScanContext): void {
    this.context = ctx.context
    this.nextAction = ctx.nextAction
    this.lastScan = null
    this.sound = null
    this.pendingCode = null
    this.claim()
  }

  unmount(): void {
    this.mounted = false
    this.suspendedBy = null
    this.inFlight = false
    this.pendingCode = null
    this.target.blur()
    this.emit()
  }

  /** A sheet (candidate picker, evidence) legitimately owns focus while it is open. */
  suspend(owner: string): void {
    this.suspendedBy = owner
    this.emit()
  }

  resume(): void {
    this.suspendedBy = null
    this.claim()
  }

  /** Called on the field's blur event. This is the retention rule. */
  onBlur(): void {
    if (!this.mounted || this.suspendedBy) return
    this.claim()
  }

  /** Enter, or the scanner's trailing newline. */
  submit(): void {
    const code = normaliseCode(this.target.read())
    this.target.clear()
    if (!code) {
      this.claim()
      return
    }
    this.fire(code)
  }

  /** A code read continuously off the camera stream. Same path, no tap. */
  decoded(raw: string): void {
    const code = normaliseCode(raw)
    if (!code) return
    if (this.pendingCode === code) return
    this.fire(code)
  }

  /** The verdict from the API, which is what actually moves the halo. */
  settle(s: Settlement): void {
    this.inFlight = false
    this.pendingCode = null
    this.lastScan = { code: this.lastScan?.code ?? '', result: s.result, message: s.message }
    this.sound = s.result
    this.nextAction =
      s.nextAction ?? (s.result === 'reject' ? (this.options.recoveryAction ?? 'Scan again') : this.nextAction)
    this.claim()
  }

  private fire(code: string): void {
    this.pendingCode = code
    this.inFlight = true
    this.lastScan = { code, result: 'accept', message: '' }
    this.claim()
    this.options.onScan?.(code)
  }

  private claim(): void {
    if (this.mounted && !this.suspendedBy) this.target.focus()
    this.emit()
  }

  private emit(): void {
    this.options.onChange?.(this.state())
  }
}
